/** Validates stored credentials without letting obsolete bootstrap work restore a session. */
import { useEffect, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth.store';
import { getAccessToken, getRefreshToken, isTokenExpired, refreshAccessToken } from '@/utils/token';
import { getSessionOwner, ownsSession, onSessionReplacement } from '@/utils/session';
import * as authApi from '@/api/auth.api';

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { setAuth, setLoading, logout } = useAuthStore();
  const queryClient = useQueryClient();
  useEffect(() => onSessionReplacement(() => queryClient.clear()), [queryClient]);

  useEffect(() => {
    let active = true;
    const owner = getSessionOwner();
    const current = () => active && ownsSession(owner);
    const initialize = async () => {
      setLoading(true);
      const accessToken = getAccessToken();
      const refreshToken = getRefreshToken();
      if (!accessToken && !refreshToken) {
        if (current()) logout();
        return;
      }
      try {
        if (accessToken && !isTokenExpired(accessToken)) {
          try {
            const user = await authApi.getCurrentUser();
            if (!current()) return;
            setAuth(user, {
              accessToken: getAccessToken() ?? accessToken,
              refreshToken: getRefreshToken() ?? refreshToken ?? '',
            });
            return;
          } catch {
            if (!current()) return;
          }
        }
        if (!refreshToken) {
          if (current()) logout();
          return;
        }
        // StrictMode cancels its first consumer, not the shared single-use refresh.
        const tokens = await refreshAccessToken(authApi.refreshToken);
        if (!current()) return;
        const user = await authApi.getCurrentUser();
        if (!current()) return;
        setAuth(user, tokens);
      } catch {
        if (current()) logout();
      }
    };
    void initialize();
    return () => {
      active = false;
    };
  }, [setAuth, setLoading, logout]);
  return <>{children}</>;
};
